import { useEffect, useState } from "react";
import OtpChannelNote, { OtpSmsLink, OtpChannelPicker, channelOf } from "../components/OtpChannelNote";
import { Icon } from "../components/Icon";
import { authService } from "../services/authService";
import { clearLegacyAuth, saveAuth, homeForRole } from "../utils/auth";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { fillText } from "../utils/leadDisplay";
import { track } from "../utils/analytics";
import { AuthSplit } from "../components/home/DesktopAtoms";
import { CallBackSheet } from "../components/CallBack";

const PHONE_RE = /^[6-9]\d{9}$/;
const normalizeDigits = (v) => v.replace(/\D/g, "").slice(0, 10);
const sanitizeUsername = (v) => v.replace(/[^a-zA-Z0-9 ]/g, "");
/* Applied only where a password is CREATED - never on sign-in. Matches the
   server, which has required 8 to 64 characters since June. */
const isValidPassword = (v) => v.length >= 8 && v.length <= 64;

const OTP_ENABLED = process.env.REACT_APP_OTP_ENABLED !== "false";

// ── Atoms ────────────────────────────────────────────────────
function PhoneInput({ value, onChange, placeholder, error, touched }) {
  const { t } = useLanguage();
  const valid = PHONE_RE.test(value);
  const showErr = touched && value.length > 0 && !valid;
  return (
    <div>
      <div className={`flex items-center border rounded-[12px] overflow-hidden transition-all ${
        showErr ? "border-[#DC3545] bg-red-50"
          : touched && valid ? "border-[#7BAE00] bg-[#F6FAEC]"
          : "border-[#E5E7EB] focus-within:border-[#7BAE00] bg-white"
      }`}>
        <span className="px-3 py-2.5 text-[11px] font-bold text-[#6B7280] bg-[#F5F5F5] border-r border-[#E5E7EB] shrink-0">+91</span>
        <input
          placeholder={placeholder || t("lg_tenDigit")}
          value={value}
          onChange={(e) => onChange(normalizeDigits(e.target.value))}
          maxLength={10}
          inputMode="numeric"
          autoComplete="off"
          className="flex-1 px-3 py-2.5 text-xs bg-transparent focus:outline-none"
        />
        {touched && valid && (
          <span className="px-2 text-[#7BAE00] text-sm shrink-0">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </span>
        )}
      </div>
      {showErr && (
        <p className="text-[10px] text-[#DC3545] mt-1 font-semibold pl-1">
          {value.length < 10 ? (10 - value.length === 1 ? t("lg_oneMoreDigit") : fillText(t("lg_moreDigits"), { n: 10 - value.length }))
            : t("lg_startDigit")}
        </p>
      )}
    </div>
  );
}

function ErrBanner({ msg }) {
  if (!msg) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-150 fade-up">
      <span className="text-[#DC3545] shrink-0"><Icon name="alertTri" size={14} stroke="#DC3545" /></span>
      <p className="text-[10.5px] font-semibold text-[#DC3545] leading-relaxed">{msg}</p>
    </div>
  );
}

// ── Forgot-password flow (real 3-step OTP reset) ───────────────
function ForgotFlow({ initialPhone, autoInitMessage, onClose, onSuccess }) {
  const { t } = useLanguage();
  const [step, setStep]               = useState("PHONE");
  const [phone, setPhone]             = useState(initialPhone || "");
  const [otp, setOtp]                 = useState("");
  const [newPw, setNewPw]             = useState("");
  const [showNewPw, setShowNewPw]     = useState(false);
  const [resetToken, setResetToken]   = useState("");
  const [verifiedToken, setVerifiedToken] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [cooldown, setCooldown]       = useState(0);
  const [error, setError]             = useState("");
  const [info]                        = useState(autoInitMessage || "");
  const [loading, setLoading]         = useState(false);

  useEffect(() => {
    if (autoInitMessage && initialPhone && PHONE_RE.test(initialPhone)) {
      handleInit();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  async function handleInit() {
    setError(""); setLoading(true);
    try {
      const input = phone.trim();
      if (!input) { setError(t("lg_enterMobile")); return; }
      const res = await authService.forgotPasswordInit(input);
      setResetToken(res.resetToken);
      setMaskedPhone(res.maskedPhone);
      setStep("OTP");
      setCooldown(60);
      setOtp("");
    } catch (e) { setError(e?.message || t("lg_otpSendFail")); }
    finally { setLoading(false); }
  }

  async function handleVerify() {
    setError(""); setLoading(true);
    try {
      if (!otp) { setError(t("lg_enterOtp")); return; }
      const res = await authService.forgotPasswordVerify(resetToken, otp);
      setVerifiedToken(res.verifiedToken);
      setStep("RESET");
    } catch (e) { setError(e?.message || t("lg_otpInvalid")); }
    finally { setLoading(false); }
  }

  async function handleResend() {
    if (cooldown > 0) return;
    setError(""); setLoading(true);
    try {
      const res = await authService.forgotPasswordInit(phone.trim());
      setResetToken(res.resetToken);
      setCooldown(60);
      setOtp("");
    } catch (e) { setError(e?.message || t("lg_otpResendFail")); }
    finally { setLoading(false); }
  }

  async function handleReset() {
    setError(""); setLoading(true);
    try {
      if (!newPw || newPw.length < 8) { setError(t("lg_pwMin")); return; }
      if (newPw.length > 64) { setError(t("lg_pwMax")); return; }
      const res = await authService.forgotPasswordReset(verifiedToken, newPw);
      onSuccess(res);
    } catch (e) { setError(e?.message || t("lg_resetFail")); }
    finally { setLoading(false); }
  }

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-[#1A1A2E]">
          {step === "PHONE" ? t("lg_resetTitle") : step === "OTP" ? t("lg_verifyOtp") : t("lg_setNewPw")}
        </h3>
        <button onClick={onClose} className="text-[#7B8794] hover:text-[#1F2937] text-lg leading-none">×</button>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-150">
          <span className="text-[#DC3545] shrink-0"><Icon name="alertTri" size={14} stroke="#DC3545" /></span>
          <p className="text-[10.5px] font-semibold text-[#DC3545] leading-relaxed">{error}</p>
        </div>
      )}

      {info && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-50 border border-blue-100">
          <p className="text-[10.5px] font-bold text-blue-700 leading-relaxed">{info}</p>
        </div>
      )}

      {step === "PHONE" && (
        <>
          <p className="text-[10.5px] text-[#6B7280] leading-relaxed">{t("lg_forgotIntro")}</p>
          <div>
            <label className="form-label mb-1">{t("lg_mobile")}</label>
            <div className="flex items-center border border-[#E5E7EB] focus-within:border-[#7BAE00] rounded-[12px] overflow-hidden bg-white">
              <span className="px-3 py-2.5 text-[11px] font-bold text-[#6B7280] bg-[#F5F5F5] border-r border-[#E5E7EB] shrink-0">+91</span>
              <input placeholder={t("lg_tenDigit")} value={phone}
                onChange={(e) => setPhone(normalizeDigits(e.target.value))}
                maxLength={10} inputMode="numeric" autoComplete="off"
                className="flex-1 px-3 py-2.5 text-xs bg-transparent focus:outline-none"
                onKeyDown={(e) => e.key === "Enter" && PHONE_RE.test(phone) && handleInit()} />
            </div>
          </div>
          <button onClick={handleInit} disabled={loading || !PHONE_RE.test(phone)}
            className="w-full h-[40px] rounded-full bg-[#7BAE00] hover:bg-[#689300] text-white text-[11px] font-bold shadow-sm transition-all flex items-center justify-center active:scale-[0.98] disabled:opacity-40">
            {loading ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> {t("lg_sending")}</> : t("lg_sendOtp")}
          </button>
        </>
      )}

      {step === "OTP" && (
        <>
          <p className="text-[10.5px] text-[#6B7280] leading-relaxed">{fillText(t("lg_otpSentTo"), { phone: maskedPhone })}</p>
          <div>
            <div className="flex gap-2">
              <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder={t("lg_otp6")} className="input flex-1 h-[36px] rounded-[10px] text-xs font-mono text-center tracking-widest focus:border-[#7BAE00]"
                inputMode="numeric" maxLength={6}
                onKeyDown={(e) => e.key === "Enter" && otp.length === 6 && handleVerify()} />
              <button onClick={handleResend} disabled={cooldown > 0 || loading}
                className="px-3 rounded-[10px] border border-[#EAEFF5] text-[10px] font-bold text-[#7B8794] hover:text-[#7BAE00] disabled:opacity-40 shrink-0 bg-white">
                {cooldown > 0 ? fillText(t("lg_resendIn"), { n: cooldown }) : t("lg_resend")}
              </button>
            </div>
            {cooldown > 0 && (
              <div className="w-full bg-[#E5E7EB] rounded-full h-1 overflow-hidden mt-2">
                <div className="h-full bg-[#7BAE00] rounded-full transition-all duration-1000" style={{ width: `${(cooldown / 60) * 100}%` }} />
              </div>
            )}
          </div>
          <button onClick={handleVerify} disabled={loading || otp.length < 4}
            className="w-full h-[40px] rounded-full bg-[#7BAE00] hover:bg-[#689300] text-white text-[11px] font-bold shadow-sm transition-all flex items-center justify-center active:scale-[0.98] disabled:opacity-40">
            {loading ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> {t("lg_verifying")}</> : t("lg_verifyOtp")}
          </button>
        </>
      )}

      {step === "RESET" && (
        <>
          <p className="text-[10.5px] text-[#6B7280] leading-relaxed">{t("lg_otpDone")}</p>
          <div>
            <label className="form-label mb-1">{t("lg_newPw")}</label>
            <div className="flex items-center border border-[#E5E7EB] focus-within:border-[#7BAE00] rounded-[12px] overflow-hidden bg-white">
              <input type={showNewPw ? "text" : "password"} value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder={t("lg_pwHint")} className="flex-1 px-3 py-2.5 text-xs bg-transparent focus:outline-none"
                minLength={8} maxLength={64} autoComplete="new-password"
                onKeyDown={(e) => e.key === "Enter" && newPw.length >= 8 && handleReset()} />
              <button type="button" onClick={() => setShowNewPw((p) => !p)}
                className="px-3 text-[#6B7280] shrink-0 hover:text-[#7BAE00] transition-colors">
                <EyeIcon open={showNewPw} />
              </button>
            </div>
          </div>
          <button onClick={handleReset} disabled={loading || newPw.length < 8}
            className="w-full h-[40px] rounded-full bg-[#7BAE00] hover:bg-[#689300] text-white text-[11px] font-bold shadow-sm transition-all flex items-center justify-center active:scale-[0.98] disabled:opacity-40">
            {loading ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> {t("lg_saving")}</> : t("lg_setNewPw")}
          </button>
        </>
      )}
    </div>
  );
}


// ── Eye icon for password toggle ─────────────────────────────
function EyeIcon({ open, size = 14 }) {
  return open ? (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#7B8794" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#7B8794" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

// ── Main ─────────────────────────────────────────────────────
export default function Login() {
  const { t } = useLanguage();
  const [mode, setMode] = useState("LOGIN");
  const [step, setStep] = useState("LOGIN");

  // Login — password or OTP
  const [loginMode, setLoginMode]     = useState("PASSWORD"); // "PASSWORD" | "OTP"
  const [identifier, setIdentifier]   = useState("");
  const [password, setPassword]       = useState("");
  const [showPw, setShowPw]           = useState(false);
  const [forgotConfig, setForgotConfig] = useState(null); // { phone, autoInitMessage }

  // OTP login state
  const [loginPhone, setLoginPhone]           = useState("");
  const [loginOtp, setLoginOtp]               = useState("");
  const [loginOtpSent, setLoginOtpSent]       = useState(false);
  const [loginOtpCooldown, setLoginOtpCooldown] = useState(0);

  // Signup
  const [phone, setPhone]       = useState("");
  const [email, setEmail]       = useState("");
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [otp, setOtp]           = useState("");
  const [signupPw, setSignupPw] = useState("");
  const [confirmSignupPw, setConfirmSignupPw] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [otpSentFor, setOtpSentFor]   = useState({ phone: "", username: "" });
  const [, setResendBlocked] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState({ available: true, suggested: "" });
  const [phoneStatus, setPhoneStatus]       = useState({ exists: false });
  const [otpSentMsg, setOtpSentMsg] = useState("");
  // Where the last code went - WhatsApp or SMS (D6) - for OTP login and for sign-up.
  const [loginCh, setLoginCh] = useState({ channel: null, at: 0 });
  const [signupCh, setSignupCh] = useState({ channel: null, at: 0 });

  // Touched
  const [phoneTouched, setPhoneTouched] = useState(false);

  const [error, setError]   = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastCheckedPhone, setLastCheckedPhone] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const { redirectTo, payload } = location.state || {};
  // Back to the car or driver that asked for the sign-in; otherwise the person's own start.
  const landing = (res) => redirectTo || homeForRole(res?.user?.role, "/");
  /* Why the login was asked, for "From visit to call" on Admin > Analytics (Oct 2026):
     a Call, a hire driver, a quotation, or the person's own choice. */
  const why = payload?.callAfterLogin ? "call" : String(redirectTo || "").startsWith("/drivers") ? "hire"
    : String(redirectTo || "").startsWith("/car/") ? "car" : redirectTo === "/request-quotation" ? "quote" : "own";
  const mark = (event, extra = {}) => track(event, { intent: why, ...extra });
  const signedIn = (how) => mark("login_done", { method: how });
  const [callBack, setCallBack] = useState(false);

  useEffect(() => { clearLegacyAuth(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { mark("login_viewed"); }, []);

  // OTP login cooldown
  useEffect(() => {
    if (loginOtpCooldown <= 0) return;
    const t = setInterval(() => setLoginOtpCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [loginOtpCooldown]);

  // Signup OTP countdown
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setInterval(() => setOtpCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [otpCooldown]);

  // Username availability
  useEffect(() => {
    if (mode !== "SIGNUP") return;
    const name = username.trim();
    if (!name) { setUsernameStatus({ available: true, suggested: "" }); return; }
    const h = setTimeout(async () => {
      try { const r = await authService.checkDisplayName(name); setUsernameStatus({ available: !!r.available, suggested: r.suggested || "" }); }
      catch { setUsernameStatus({ available: true, suggested: "" }); }
    }, 400);
    return () => clearTimeout(h);
  }, [username, mode]);

  // Phone existence check
  useEffect(() => {
    if (mode !== "SIGNUP") return;
    if (!PHONE_RE.test(phone)) { setPhoneStatus({ exists: false }); return; }
    const h = setTimeout(async () => {
      try { const r = await authService.checkUser(phone); setPhoneStatus({ exists: !!r.registered || !!r.role }); }
      catch { setPhoneStatus({ exists: false }); }
    }, 400);
    return () => clearTimeout(h);
  }, [phone, mode]);

  // Manual send OTP for signup
  async function sendSignupOtp() {
    if (!username.trim()) { setError(t("lg_enterUsername")); return; }
    if (!usernameStatus.available) { setError(t("lg_otherUsername")); return; }
    if (!PHONE_RE.test(phone)) { setError(t("lg_validMobile")); return; }
    if (phoneStatus.exists) { setError(t("lg_hasAccount")); return; }
    setError(""); setInfoMsg("");
    try {
      setSignupCh({ channel: channelOf(await authService.sendOtp(phone)), at: Date.now() });
      mark("login_otp_sent", { method: "signup" });
      setStep("SIGNUP_OTP");
      setOtp("");
      setOtpCooldown(60);
      setOtpSentFor({ phone, username: username.trim() });
      setOtpSentMsg(fillText(t("lg_otpSentShort"), { phone }));
      setTimeout(() => setOtpSentMsg(""), 4000);
    } catch (e) { setError(e?.message || t("lg_otpSendFail")); }
  }

  async function autoRouteToSignup(targetPhone) {
    setInfoMsg(t("lg_noAccount"));
    setMode("SIGNUP"); setStep("SIGNUP_OTP"); setPhone(targetPhone);
    setOtpSentFor({ phone: targetPhone, username: "" });
    try {
      setSignupCh({ channel: channelOf(await authService.sendOtp(targetPhone)), at: Date.now() });
      mark("login_otp_sent", { method: "signup" });
      setOtpCooldown(60);
      setOtpSentMsg(fillText(t("lg_otpSentShort"), { phone: targetPhone }));
      setTimeout(() => setOtpSentMsg(""), 4000);
    } catch (e) {
      setInfoMsg("");
      setError(e?.message || t("lg_autoOtpFail"));
    }
  }

  const isNumeric = /^\d+$/.test(identifier);

  // OTP login handlers
  async function sendLoginOtp() {
    setError(""); setInfoMsg("");
    if (!PHONE_RE.test(loginPhone)) { setError(t("lg_validMobile")); return; }
    try {
      try {
        const r = await authService.checkUser(loginPhone);
        if (r) {
          if (!r.exists || (!r.registered && !r.role)) { autoRouteToSignup(loginPhone); return; }
          if (r.exists && !r.hasPassword) {
            setInfoMsg(t("lg_noPwYet"));
          }
        }
      } catch (err) {
        console.warn("checkUser failed, falling back to normal OTP flow", err);
      }

      setLoginCh({ channel: channelOf(await authService.sendOtp(loginPhone, "LOGIN")), at: Date.now() });
      mark("login_otp_sent", { method: "login" });
      setLoginOtpSent(true);
      setLoginOtp("");
      setLoginOtpCooldown(60);
    } catch (e) { setError(e?.message || t("lg_otpSendFail")); }
  }

  async function submitOtpLogin() {
    setError(""); setInfoMsg(""); setLoading(true);
    try {
      if (!loginOtp) { setError(t("lg_enterOtpSent")); return; }
      const res = await authService.verifyOtp(loginPhone, loginOtp, "LOGIN");
      
      if (res.token && res.user) {
        saveAuth(res);
        signedIn("otp");
        await refresh();
        navigate(landing(res), { replace: true, state: payload || null });
        return;
      }
      
      // If verification succeeded (no error thrown) but no token, they need to set a password
      mark("login_new_number");
      navigate("/set-password", { state: { phone: loginPhone, redirectTo, payload } });
    } catch (e) { mark("login_otp_failed", { method: "login" }); setError(e?.message || t("lg_otpInvalid")); }
    finally { setLoading(false); }
  }

  async function resendLoginOtp() {
    if (loginOtpCooldown > 0) return;
    try { setLoginCh({ channel: channelOf(await authService.sendOtp(loginPhone, "LOGIN")), at: Date.now() }); setLoginOtpCooldown(60); }
    catch (e) { setError(e?.message || t("lg_otpResendFail")); }
  }

  async function submitLogin() {
    setError(""); setInfoMsg(""); setLoading(true);
    try {
      const input = identifier.trim();
      if (!input) { setError(t("lg_enterUserOrMobile")); return; }
      if (!password) { setError(t("lg_enterPwMsg")); return; }
      /* No length rule on sign-in. The password already exists, so checking its
         shape here cannot make anything safer - and it would lock out any account
         whose password predates the current rule. The server decides. */
      if (isNumeric && !PHONE_RE.test(input)) { setError(t("lg_validMobile")); return; }
      if (isNumeric) {
        try {
          const r = await authService.checkUser(input);
          if (r && (!r.exists || (!r.registered && !r.role))) { autoRouteToSignup(input); return; }
          if (r && r.exists && !r.hasPassword) {
            setForgotConfig({ phone: input, autoInitMessage: t("lg_setPwFor") });
            return;
          }
        } catch (err) {
          console.warn("checkUser failed, falling back to normal login flow", err);
        }
      }
      const res = await authService.login(input, password);
      saveAuth(res);
      signedIn("password");
      await refresh();
      navigate(landing(res), { replace: true, state: payload || null });
    } catch {
      setError(t("lg_badLogin"));
    } finally { setLoading(false); }
  }

  async function verifySignupOtp() {
    setError("");
    if (otpCooldown === 0) { mark("login_otp_failed", { method: "expired" }); setError(t("lg_otpExpired")); return; }
    if (!otp) { setError(t("lg_enterOtpSent")); return; }
    try { await authService.verifyOtp(phone, otp); mark("login_new_number"); setStep("SIGNUP_PASSWORD"); }
    catch { mark("login_otp_failed", { method: "signup" }); setError(t("lg_otpInvalid")); }
  }

  async function submitSignupPassword() {
    setError(""); setInfoMsg(""); setLoading(true);
    try {
      if (!PHONE_RE.test(phone)) { setError(t("lg_validMobile")); return; }
      if (!isValidPassword(signupPw)) { setError(t("lg_pwRange")); return; }
      if (!OTP_ENABLED && signupPw !== confirmSignupPw) { setError(t("lg_pwMismatch")); return; }
      if (!OTP_ENABLED && !email.trim()) { setError(t("lg_enterEmail")); return; }
      if (!OTP_ENABLED && !fullName.trim()) { setError(t("lg_enterFullName")); return; }
      if (!acceptTerms) { setError(t("lg_acceptFirst")); return; }

      if (OTP_ENABLED) {
        if (!username.trim()) { setError(t("lg_enterUsername")); return; }
        await authService.setDisplayName(phone, username.trim());
      }

      const res = await authService.setPassword(phone, signupPw, OTP_ENABLED ? undefined : email.trim(), OTP_ENABLED ? undefined : fullName.trim());
      saveAuth(res);
      signedIn("signup");
      await refresh();
      navigate(landing(res), { replace: true, state: payload || null });
    } catch (e) { setError(e?.message || t("lg_createFail")); }
    finally { setLoading(false); }
  }

  async function resendOtp() {
    if (otpCooldown > 0) { setResendBlocked(true); setTimeout(() => setResendBlocked(false), 400); return; }
    try { setSignupCh({ channel: channelOf(await authService.sendOtp(phone)), at: Date.now() }); setOtpCooldown(60); }
    catch (e) { setError(e?.message || t("lg_otpResendFail")); }
  }

  function switchToSignup() { setMode("SIGNUP"); setStep(OTP_ENABLED ? "SIGNUP_OTP" : "SIGNUP_PASSWORD"); setError(""); setInfoMsg(""); }
  function switchToLogin()  { setMode("LOGIN");  setStep("LOGIN");       setError(""); setInfoMsg(""); }

  return (
    <div className="page-root keep-size bg-[#F5F7F9] min-h-screen lg:!pb-0">
      <AuthSplit title={t("lg_brand")} sub={t("dk_tagline")}
        points={[["shield", t("dk_trustCars")], ["rupee", t("dk_trustPrice")], ["phone", t("dk_trustCallOwner")]]}>
      <div className="w-full max-w-full px-4 space-y-[6px] pt-[10px] pb-[80px] lg:pb-0">

        {/* Back arrow */}
        <button onClick={() => navigate(-1)}
          className="w-8 h-8 rounded-full bg-white border border-[#EAEFF5] flex items-center justify-center shadow-sm hover:bg-[#F9FBFA] transition-all active:scale-95 text-[#7BAE00]">
          <Icon name="arrowLeft" size={12} strokeWidth={3} />
        </button>

        {/* Brand header - compact */}
        <div className="text-center py-1">
          <p className="brand-title text-[#1F2937] text-[17px] font-extrabold leading-tight">{t("lg_brand")}</p>
          <p className="brand-subtitle text-[#7B8794] text-[10px] font-medium mt-0.5">{t("lg_tagline")}</p>
        </div>

        {/* Came here to call (a car, a driver)? No login: the team calls back (mock CallBack_1). */}
        {(why === "call" || why === "car" || why === "hire") && (
          <button type="button" onClick={() => setCallBack(true)}
            className="w-full min-h-[42px] rounded-[14px] border-[1.5px] border-[#7BAE00] bg-[#F6FAEC] text-[#3F5E00] text-[12.5px] font-extrabold active:scale-[0.99]">
            {t("cbk_noLogin")}
          </button>
        )}
        {callBack && (
          <CallBackSheet onClose={() => setCallBack(false)} context={{
            page: "login", carId: payload?.car?.carId || null,
            carLabel: payload?.car ? String(payload.car.carDetails?.model || payload.car.carModel || "").split(" - ")[0] || null : payload?.returnToDriver ? "Alting driver" : null,
            driverId: payload?.returnToDriver || null,
            fromLocation: payload?.fromLocation || payload?.returnState?.fromLocation, toLocation: payload?.toLocation || payload?.returnState?.toLocation,
            fromDateTime: payload?.fromDateTime || "", tripType: payload?.tripType || null }} />
        )}

        {/* Tab switcher */}
        <div className="flex rounded-[14px] border border-[#EAEFF5] overflow-hidden bg-white shadow-sm p-1">
          {["LOGIN", "SIGNUP"].map((m) => (
            <button key={m} onClick={m === "LOGIN" ? switchToLogin : switchToSignup}
              className={`flex-1 py-2 rounded-[10px] text-[10px] font-extrabold uppercase tracking-widest transition-all ${
                mode === m ? "bg-[#7BAE00] text-white shadow-sm shadow-[#7BAE00]/10" : "text-[#7B8794] hover:bg-[#F9FBFA] hover:text-[#1F2937]"
              }`}>
              {m === "LOGIN" ? t("lg_logIn") : t("lg_signUp")}
            </button>
          ))}
        </div>

        {/* Forgot password modal */}
        {forgotConfig && (
          <div className="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4" onClick={() => setForgotConfig(null)}>
            <div className="bg-white rounded-[18px] w-full max-w-sm shadow-[0_4px_24px_rgba(0,0,0,0.12)] border border-[#EAEFF5] overflow-hidden fade-up" onClick={e => e.stopPropagation()}>
              <ForgotFlow initialPhone={forgotConfig.phone} autoInitMessage={forgotConfig.autoInitMessage}
                onClose={() => setForgotConfig(null)}
                onSuccess={(res) => { saveAuth(res); signedIn("reset"); refresh().then(() => navigate(landing(res), { replace: true, state: payload || null })); }} />
            </div>
          </div>
        )}

        <div className="bg-white rounded-[18px] border border-[#EAEFF5] shadow-[0_2px_8px_rgba(15,23,42,0.028)] p-4 space-y-3">
          <ErrBanner msg={error} />
          
          {infoMsg && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-50 border border-blue-100 fade-up">
              <p className="text-[10.5px] font-bold text-blue-700">{infoMsg}</p>
            </div>
          )}
          
          {/* OTP sent success toast */}
          {otpSentMsg && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#F6FAEC] border border-[#7BAE00]/25 fade-up">
              <Icon name="checkCircle" size={14} stroke="#7BAE00" />
              <p className="text-[10px] font-bold text-[#5E8700]">{otpSentMsg}</p>
            </div>
          )}

          {/* ── LOGIN ─────────────────────────────────── */}
          {mode === "LOGIN" && (
            <>
              {/* Password / OTP toggle */}
              <div className="flex rounded-[10px] border border-[#EAEFF5] overflow-hidden bg-[#F9FBFA] p-0.5">
                {["PASSWORD", "OTP"].map((m) => (
                  <button key={m} type="button"
                    onClick={() => { setLoginMode(m); setError(""); setInfoMsg(""); setLoginOtpSent(false); setLoginOtp(""); setLoginPhone(identifier && PHONE_RE.test(identifier) ? identifier : ""); }}
                    className={`flex-1 py-1.5 rounded-[8px] text-[10px] font-bold transition-all ${
                      loginMode === m ? "bg-white text-[#1F2937] shadow-sm" : "text-[#7B8794]"
                    }`}>
                    {m === "PASSWORD" ? t("lg_password") : t("lg_otpLogin")}
                  </button>
                ))}
              </div>

              {/* PASSWORD LOGIN */}
              {loginMode === "PASSWORD" && (
                <>
                  <div>
                    <label className="form-label mb-1">{t("lg_userOrMobile")}</label>
                    <div className="flex items-center border border-[#E5E7EB] focus-within:border-[#7BAE00] rounded-[12px] overflow-hidden transition-all bg-white">
                      {isNumeric && identifier.length > 0 && (
                        <span className="px-3 py-2.5 text-[11px] font-bold text-[#6B7280] bg-[#F5F5F5] border-r border-[#E5E7EB] shrink-0">+91</span>
                      )}
                      <input
                        placeholder={t("lg_userOrMobilePh")}
                        value={identifier}
                        onChange={(e) => {
                          const v = e.target.value;
                          setIdentifier(/^\d+$/.test(v) || v === "" ? normalizeDigits(v) : v);
                        }}
                        onBlur={async () => {
                          if (isNumeric && PHONE_RE.test(identifier) && identifier !== lastCheckedPhone) {
                            setLastCheckedPhone(identifier);
                            try {
                              const r = await authService.checkUser(identifier);
                              if (!r.exists || (!r.registered && !r.role)) { 
                                autoRouteToSignup(identifier);
                              }
                            } catch {}
                          }
                        }}
                        className="flex-1 px-3 py-2.5 text-xs bg-transparent focus:outline-none"
                        maxLength={isNumeric ? 10 : undefined}
                        inputMode={isNumeric ? "numeric" : "text"}
                        autoComplete="off"
                        onKeyDown={(e) => e.key === "Enter" && submitLogin()}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="form-label mb-1">{t("lg_password")}</label>
                    <div className="flex items-center border border-[#E5E7EB] focus-within:border-[#7BAE00] rounded-[12px] overflow-hidden bg-white">
                      <input
                        type={showPw ? "text" : "password"}
                        placeholder={t("lg_enterPw")}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="flex-1 px-3 py-2.5 text-xs bg-transparent focus:outline-none"
                        autoComplete="current-password"
                        onKeyDown={(e) => e.key === "Enter" && submitLogin()}
                      />
                      <button type="button" onClick={() => setShowPw((p) => !p)}
                        className="px-3 text-[#6B7280] shrink-0 hover:text-[#7BAE00] transition-colors">
                        <EyeIcon open={showPw} />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <button type="button" onClick={() => setForgotConfig({ phone: PHONE_RE.test(identifier) ? identifier : "", autoInitMessage: "" })}
                      className="text-[10px] text-[#7BAE00] font-extrabold hover:underline pl-1">
                      {t("lg_forgot")}
                    </button>
                  </div>

                  <button onClick={submitLogin} disabled={loading}
                    className="w-full h-[40px] rounded-full bg-[#7BAE00] hover:bg-[#689300] text-white text-[11px] font-bold shadow-sm shadow-[#7BAE00]/10 transition-all flex items-center justify-center active:scale-[0.98]">
                    {loading ? (
                      <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> {t("lg_signingIn")}</>
                    ) : t("lg_signIn")}
                  </button>
                </>
              )}

              {/* OTP LOGIN */}
              {loginMode === "OTP" && (
                <>
                  <div>
                    <label className="form-label mb-1">{t("lg_mobile")}</label>
                    <div className="flex items-center border border-[#E5E7EB] focus-within:border-[#7BAE00] rounded-[12px] overflow-hidden transition-all bg-white">
                      <span className="px-3 py-2.5 text-[11px] font-bold text-[#6B7280] bg-[#F5F5F5] border-r border-[#E5E7EB] shrink-0">+91</span>
                      <input
                        placeholder={t("lg_tenDigit")}
                        value={loginPhone}
                        onChange={(e) => { setLoginPhone(normalizeDigits(e.target.value)); setLoginOtpSent(false); setLoginOtp(""); }}
                        onBlur={async () => {
                          if (PHONE_RE.test(loginPhone) && loginPhone !== lastCheckedPhone) {
                            setLastCheckedPhone(loginPhone);
                            try {
                              const r = await authService.checkUser(loginPhone);
                              if (!r.exists || (!r.registered && !r.role)) { 
                                autoRouteToSignup(loginPhone);
                              }
                            } catch {}
                          }
                        }}
                        maxLength={10} inputMode="numeric" autoComplete="off"
                        className="flex-1 px-3 py-2.5 text-xs bg-transparent focus:outline-none"
                      />
                    </div>
                    <p className="text-[10px] text-[#6B7280] leading-relaxed mt-1.5 pl-1">{t("lg_waNotice")}</p>
                  </div>

                  {!loginOtpSent && <OtpChannelPicker className="mb-2.5" />}
                  {!loginOtpSent ? (
                    <button onClick={sendLoginOtp}
                      disabled={!PHONE_RE.test(loginPhone)}
                      className="w-full h-[40px] rounded-full bg-[#7BAE00] hover:bg-[#689300] text-white text-[11px] font-bold shadow-sm shadow-[#7BAE00]/10 transition-all flex items-center justify-center active:scale-[0.98] disabled:opacity-40">
                      {t("lg_sendOtp")}
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="form-label mb-1">OTP <span className="text-[#DC3545]">*</span></label>
                        <OtpChannelNote channel={loginCh.channel} phone={loginPhone}
                          fallback={<p className="text-[9.5px] text-[#6B7280] mb-1.5 pl-1">{fillText(t("lg_codeSentTo"), { phone: loginPhone })}</p>} />
                        <div className="flex gap-2">
                          <input value={loginOtp}
                            onChange={(e) => setLoginOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder={t("lg_otp6")} className="input flex-1 h-[36px] rounded-[10px] text-xs font-mono text-center tracking-widest focus:border-[#7BAE00]"
                            inputMode="numeric" maxLength={6}
                            onKeyDown={(e) => e.key === "Enter" && loginOtp.length === 6 && submitOtpLogin()} />
                          <button onClick={resendLoginOtp} disabled={loginOtpCooldown > 0}
                            className="px-3 rounded-[10px] border border-[#EAEFF5] text-[10px] font-bold text-[#7B8794] hover:text-[#7BAE00] disabled:opacity-40 shrink-0 bg-white">
                            {loginOtpCooldown > 0 ? fillText(t("lg_resendIn"), { n: loginOtpCooldown }) : t("lg_resend")}
                          </button>
                        </div>
                        <OtpSmsLink channel={loginCh.channel} sentAt={loginCh.at} onSms={async () => {
                          try { await authService.sendOtp(loginPhone, "LOGIN", "SMS"); setLoginCh({ channel: "SMS", at: Date.now() }); setLoginOtpCooldown(60); }
                          catch (e) { setError(e?.message || t("lg_otpResendFail")); }
                        }} />
                      </div>
                      {loginOtpCooldown > 0 && (
                        <div className="w-full bg-[#E5E7EB] rounded-full h-1 overflow-hidden">
                          <div className="h-full bg-[#7BAE00] rounded-full transition-all duration-1000" style={{ width: `${(loginOtpCooldown / 60) * 100}%` }} />
                        </div>
                      )}
                      <button onClick={submitOtpLogin} disabled={loading || loginOtp.length < 4}
                        className="w-full h-[40px] rounded-full bg-[#7BAE00] hover:bg-[#689300] text-white text-[11px] font-bold shadow-sm shadow-[#7BAE00]/10 transition-all flex items-center justify-center active:scale-[0.98] disabled:opacity-40">
                        {loading ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> {t("lg_verifying")}</> : t("lg_verifySignIn")}
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ── SIGNUP ────────────────────────────────── */}
          {mode === "SIGNUP" && (
            <>
              {OTP_ENABLED && (
                <div>
                  <label className="form-label mb-1">{t("lg_userName")} <span className="text-[#DC3545]">*</span></label>
                  <input value={username}
                    onChange={(e) => setUsername(sanitizeUsername(e.target.value))}
                    className="input h-[36px] rounded-[10px] text-xs focus:border-[#7BAE00]" autoComplete="off" />
                  {!usernameStatus.available && (
                    <p className="text-[10px] text-[#DC3545] mt-1 font-semibold pl-1">
                      {t("lg_nameTaken")}{usernameStatus.suggested ? ` ${fillText(t("lg_tryName"), { name: usernameStatus.suggested })}` : ""}
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="form-label mb-1">{t("lg_mobile")} <span className="text-[#DC3545]">*</span></label>
                <PhoneInput value={phone} onChange={(v) => { 
                  setPhone(v); 
                  setPhoneTouched(true); 
                  if (step === "SIGNUP_OTP" && v !== otpSentFor.phone) {
                    setStep("LOGIN");
                    setOtp("");
                  }
                }} touched={phoneTouched} />
                <p className="text-[10px] text-[#6B7280] leading-relaxed mt-1.5 pl-1">{t("lg_waNotice")}</p>
                {phoneStatus.exists && (
                  <div className="mt-2 p-3 rounded-xl bg-orange-50 border border-orange-100">
                    <p className="text-[10.5px] font-bold text-[#E65100] mb-2 leading-snug">{t("lg_numTaken")}</p>
                    <button type="button" onClick={() => { switchToLogin(); setIdentifier(phone); }}
                      className="w-full h-[36px] rounded-full bg-[#7BAE00] hover:bg-[#689300] text-white text-[11px] font-bold shadow-sm shadow-[#7BAE00]/10 transition-all flex items-center justify-center active:scale-95">
                      {t("lg_signInInstead")}
                    </button>
                  </div>
                )}
              </div>

              {!OTP_ENABLED && (
                <>
                  <div>
                    <label className="form-label mb-1 mt-3">{t("lg_email")} <span className="text-[#DC3545]">*</span></label>
                    <input type="email" placeholder="you@example.com" value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input h-[36px] rounded-[10px] text-xs focus:border-[#7BAE00]" autoComplete="email" />
                  </div>
                  <div>
                    <label className="form-label mb-1 mt-3">{t("lg_fullName")} <span className="text-[#DC3545]">*</span></label>
                    <input type="text" placeholder={t("od_namePh")} value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="input h-[36px] rounded-[10px] text-xs focus:border-[#7BAE00]" autoComplete="name" />
                  </div>
                </>
              )}

              {/* Send OTP button */}
              {OTP_ENABLED && step !== "SIGNUP_OTP" && step !== "SIGNUP_PASSWORD" && !phoneStatus.exists && <OtpChannelPicker className="mt-3" />}
              {OTP_ENABLED && step !== "SIGNUP_OTP" && step !== "SIGNUP_PASSWORD" && !phoneStatus.exists && (
                <button onClick={sendSignupOtp}
                  disabled={!username.trim() || !usernameStatus.available || !PHONE_RE.test(phone)}
                  className="w-full h-[40px] rounded-full bg-[#7BAE00] hover:bg-[#689300] text-white text-[11px] font-bold shadow-sm shadow-[#7BAE00]/10 transition-all flex items-center justify-center active:scale-[0.98] disabled:opacity-40 mt-3">
                  {t("lg_sendOtp")}
                </button>
              )}

              {OTP_ENABLED && step === "SIGNUP_OTP" && !phoneStatus.exists && (
                <div className="space-y-3.5">
                  <div>
                    <label className="form-label mb-1">OTP <span className="text-[#DC3545]">*</span></label>
                    <OtpChannelNote channel={signupCh.channel} phone={phone}
                      fallback={<p className="text-[9.5px] text-[#6B7280] mb-1.5 pl-1">{fillText(t("lg_codeSentTo"), { phone })}</p>} />
                    <div className="flex gap-2">
                      <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder={t("lg_enterOtp6")} className="input flex-1 h-[36px] rounded-[10px] text-xs font-mono text-center tracking-widest focus:border-[#7BAE00]" inputMode="numeric" maxLength={6}
                        onKeyDown={(e) => e.key === "Enter" && otp.length === 6 && verifySignupOtp()} />
                      <button onClick={resendOtp} disabled={otpCooldown > 0}
                        className="px-3 rounded-[10px] border border-[#EAEFF5] text-[10px] font-bold text-[#7B8794] hover:text-[#7BAE00] disabled:opacity-40 shrink-0 bg-white">
                        {otpCooldown > 0 ? fillText(t("lg_resendIn"), { n: otpCooldown }) : t("lg_resend")}
                      </button>
                    </div>
                    <OtpSmsLink channel={signupCh.channel} sentAt={signupCh.at} onSms={async () => {
                      try { await authService.sendOtp(phone, "", "SMS"); setSignupCh({ channel: "SMS", at: Date.now() }); setOtpCooldown(60); }
                      catch (e) { setError(e?.message || t("lg_otpResendFail")); }
                    }} />
                  </div>
                  {otpCooldown > 0 && (
                    <div className="w-full bg-[#E5E7EB] rounded-full h-1 overflow-hidden">
                      <div className="h-full bg-[#7BAE00] rounded-full transition-all duration-1000" style={{ width: `${(otpCooldown / 60) * 100}%` }} />
                    </div>
                  )}
                  <button onClick={verifySignupOtp} disabled={otp.length < 4}
                    className="w-full h-[40px] rounded-full bg-[#7BAE00] hover:bg-[#689300] text-white text-[11px] font-bold shadow-sm shadow-[#7BAE00]/10 transition-all flex items-center justify-center active:scale-[0.98] disabled:opacity-40">{t("lg_verifyOtp")}</button>
                </div>
              )}

              {(!OTP_ENABLED || step === "SIGNUP_PASSWORD") && !phoneStatus.exists && (
                <div className="space-y-3 mt-3">
                  <div>
                    <label className="form-label mb-1">{t("lg_setPw")}</label>
                    <div className="flex items-center border border-[#E5E7EB] focus-within:border-[#7BAE00] rounded-[12px] overflow-hidden bg-white">
                      <input type={showPw ? "text" : "password"} value={signupPw}
                        onChange={(e) => setSignupPw(e.target.value)}
                        placeholder={t("lg_pwHint")} className="flex-1 px-3 py-2.5 text-xs bg-transparent focus:outline-none"
                        minLength={8} maxLength={64} autoComplete="new-password"
                        onKeyDown={(e) => e.key === "Enter" && acceptTerms && isValidPassword(signupPw) && submitSignupPassword()} />
                      <button type="button" onClick={() => setShowPw((p) => !p)}
                        className="px-3 text-[#6B7280] shrink-0 hover:text-[#7BAE00] transition-colors">
                        <EyeIcon open={showPw} />
                      </button>
                    </div>
                  </div>

                  {!OTP_ENABLED && (
                    <div>
                      <label className="form-label mb-1 mt-1">{t("lg_confirmPw")}</label>
                      <div className="flex items-center border border-[#E5E7EB] focus-within:border-[#7BAE00] rounded-[12px] overflow-hidden bg-white">
                        <input type={showPw ? "text" : "password"} value={confirmSignupPw}
                          onChange={(e) => setConfirmSignupPw(e.target.value)}
                          placeholder={t("lg_reenterPw")} className="flex-1 px-3 py-2.5 text-xs bg-transparent focus:outline-none"
                          minLength={8} maxLength={64} autoComplete="new-password"
                          onKeyDown={(e) => e.key === "Enter" && acceptTerms && isValidPassword(signupPw) && submitSignupPassword()} />
                      </div>
                    </div>
                  )}

                  <label className="flex items-start gap-2 cursor-pointer mt-1 pl-1">
                    <input type="checkbox" checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      className="mt-0.5 accent-[#7BAE00] scale-95" />
                    <span className={`text-[10px] leading-normal ${!acceptTerms ? "text-[#6B7280]" : "text-[#1F2937]"}`}>
                      {/* The sentence in the reader's word order, with the two links where the
                          language puts them. */}
                      {t("lg_accept").split(/(\{terms\}|\{privacy\})/).map((part, i) => (
                        part === "{terms}" ? <Link key={i} to="/terms" className="text-[#7BAE00] underline font-bold">{t("lg_terms")}</Link>
                        : part === "{privacy}" ? <Link key={i} to="/privacy" className="text-[#7BAE00] underline font-bold">{t("lg_privacy")}</Link>
                        : <span key={i}>{part}</span>
                      ))}
                    </span>
                  </label>

                  <button onClick={submitSignupPassword} disabled={loading || !acceptTerms}
                    className="w-full h-[40px] rounded-full bg-[#7BAE00] hover:bg-[#689300] text-white text-[11px] font-bold shadow-sm shadow-[#7BAE00]/10 transition-all flex items-center justify-center active:scale-[0.98] disabled:opacity-40 mt-1">
                    {loading ? t("lg_creating") : t("lg_create")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Partner CTA */}
        <div className="bg-white rounded-[18px] border border-[#EAEFF5] p-3 shadow-[0_2px_8px_rgba(15,23,42,0.028)]">
          <p className="text-[11px] font-bold text-[#1F2937] leading-none">{t("lg_earn")}</p>
          <p className="text-[9.5px] text-[#7B8794] mt-0.5">{t("lg_joinAs")}</p>
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={() => navigate("/cab-owner/onboarding")}
              className="flex-1 h-[30px] rounded-full border border-[#7BAE00] text-[#7BAE00] hover:bg-[#F6FAEC] text-[10px] font-bold transition-all active:scale-[0.98]">
              {t("lg_cabOwner")}
            </button>
            <button type="button" onClick={() => navigate("/alting-driver/onboarding")}
              className="flex-1 h-[30px] rounded-full border border-[#7BAE00] text-[#7BAE00] hover:bg-[#F6FAEC] text-[10px] font-bold transition-all active:scale-[0.98]">
              {t("lg_hireDriver")}
            </button>
          </div>
        </div>
      </div>
      </AuthSplit>
    </div>
  );
}


